import { MehIcon } from "lucide-react";
import { isRouteErrorResponse, Link, useRouteError, useRouteLoaderData } from "react-router";
import { buttonVariants } from "~/components/ui/button";

type UserRole = "borrower" | "servicer_staff" | "originator_staff" | "investor";

const roleHomeUrls: Record<UserRole, string> = {
	borrower: "/borrower",
	investor: "/investor",
	originator_staff: "/originator",
	servicer_staff: "/servicer",
};

function getHomeUrl(roles?: UserRole[]): string {
	if (!roles || roles.length === 0) {
		return "/";
	}
	const primaryRole = roles[0];
	if (!primaryRole) {
		return "/";
	}
	return roleHomeUrls[primaryRole] ?? "/borrower";
}

function DevErrorDisplay({
	message,
	detail,
	stack,
}: {
	message: string;
	detail: string;
	stack?: string;
}) {
	return (
		<main className="space-y-4 p-4 sm:p-8">
			<div className="space-y-1">
				<h1 className="font-semibold text-lg">{message}</h1>
				<p className="wrap-break-word text-base text-muted-foreground">{detail}</p>
			</div>
			{stack && (
				<pre className="max-h-96 w-full overflow-x-auto overflow-y-auto rounded-lg bg-destructive/5 p-4 text-destructive text-sm">
					<code>{stack}</code>
				</pre>
			)}
		</main>
	);
}

export function ErrorDisplay({
	message,
	detail,
	homeUrl = "/",
}: {
	message: string;
	detail: string;
	homeUrl?: string;
}) {
	return (
		<main className="flex h-screen items-center justify-center p-6">
			<div className="mx-auto flex max-w-sm flex-col items-center gap-4 text-center">
				<div className="rounded-full bg-muted p-3">
					<MehIcon className="size-6" />
				</div>

				<div className="space-y-1">
					<h1 className="font-semibold text-lg">{message}</h1>
					<p className="text-base text-muted-foreground">{detail}</p>
				</div>

				<Link className={buttonVariants()} to={homeUrl}>
					Back to home
				</Link>
			</div>
		</main>
	);
}

export function GeneralErrorBoundary() {
	const error = useRouteError();
	const isDev = import.meta.env.DEV;

	// Try to get roles from root loader data for role-aware home URL
	const rootData = useRouteLoaderData("root") as { roles?: UserRole[] } | undefined;
	const homeUrl = getHomeUrl(rootData?.roles);

	let message = "Oops! Application Error.";
	let details = "An unexpected error occurred.";
	let stack: string | undefined;

	if (isRouteErrorResponse(error)) {
		message = error.status === 404 ? "404 Not Found" : "Error";
		details =
			error.status === 404
				? "The requested page could not be found."
				: error.statusText || details;
	} else if (isDev && error && error instanceof Error) {
		details = error.message;
		stack = error.stack;
	}

	if (isDev && stack) {
		return <DevErrorDisplay detail={details} message={message} stack={stack} />;
	}

	return <ErrorDisplay detail={details} homeUrl={homeUrl} message={message} />;
}
